// Minimal hyperscript helper — no framework. Creates DOM elements.
// Adapted from web/src/core/dom.ts for consistency across clients (rules.md: admin = vanilla TS).
type Attrs = Record<string, string | number | boolean | EventListener | undefined>;
type Child = Node | string | null | undefined | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") {
      el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "class") {
      el.className = String(v);
    } else if (k === "html") {
      el.innerHTML = String(v);
    } else {
      el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

function append(el: HTMLElement, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined) continue;
    if (Array.isArray(c)) append(el, c);
    else if (typeof c === "string") el.appendChild(document.createTextNode(c));
    else el.appendChild(c);
  }
}

export function mount(root: HTMLElement, node: Node): void {
  root.replaceChildren(node);
}

// Common UI helpers shared by feature views.
export function loading(msg = "불러오는 중…"): HTMLElement {
  return h("div", { class: "state muted" }, msg);
}

export function empty(msg: string): HTMLElement {
  return h("div", { class: "state muted" }, msg);
}

export function errorState(msg: string): HTMLElement {
  return h("div", { class: "state error" }, msg);
}

// Endpoint not implemented in the API yet — shown as a neutral (non-error) state.
export function notReadyState(detail?: string): HTMLElement {
  return h(
    "div",
    { class: "state muted" },
    h("strong", {}, "엔드포인트 준비중"),
    h("p", { class: "small" }, detail ?? "이 기능의 어드민 API가 아직 구현되지 않았습니다 (404). 구현되면 자동으로 동작합니다."),
  );
}
